/** Herald assistant bubble from AI SDK message parts. */

import { SPEC_DATA_PART_TYPE } from "@json-render/core";
import { type DataPart, useJsonRenderMessage } from "@json-render/react";
import { forwardRef, lazy, Suspense, useMemo } from "react";
import { AssistantHandraiseRow } from "@/components/ui/chat/AssistantHandraiseRow";
import ChatMarkdown from "@/components/ui/chat/ChatMarkdown";
import {
  buildMessageSegments,
  type MessagePart,
  type MessageSegment,
} from "@/lib/utils/aiChatUtils";
import { useHeraldDebugToolCallsEnabled } from "./dev-tools/heraldDebugToolCalls";
import {
  HeraldSpecRenderer,
  UnknownComponentFallback,
} from "./HeraldSpecRenderer";
import {
  DEFAULT_LABEL,
  firstRunningToolName,
  getToolLoadingLabel,
} from "./heraldToolLoadingLabels";

type SpecProp = Parameters<typeof HeraldSpecRenderer>[0]["spec"];

const HeraldToolCallsDevListLazy = import.meta.env.DEV
  ? lazy(() => import("./dev-tools/HeraldToolCallsDevList"))
  : null;

const getSegmentKey = (segment: MessageSegment, index: number) =>
  segment.kind === "text"
    ? `segment-text-${index}`
    : segment.kind === "tools"
      ? `segment-tools-${segment.tools[0]?.toolCallId ?? index}`
      : segment.kind === "spec"
        ? segment.specIndex !== undefined
          ? `segment-spec-${segment.specIndex}`
          : "message-spec"
        : `segment-${index}`;

interface AssistantMessageContentProps {
  parts: ReadonlyArray<MessagePart>;
  isLatestAssistantStreaming: boolean;
  reportDateValue?: string;
}

export const AssistantMessageContent = forwardRef<
  HTMLDivElement,
  AssistantMessageContentProps
>(({ parts, isLatestAssistantStreaming, reportDateValue }, ref) => {
  // Detect flat-spec protocol on parts, not on resolved spec data, so the branch
  // is stable even when data.spec is still null mid-stream.
  const hasFlatSpecParts = parts.some(
    (p) =>
      p.type === SPEC_DATA_PART_TYPE &&
      (p as { data?: { type?: string } }).data?.type === "flat",
  );

  // Feed only patch parts to the legacy hook; flat parts would be Object.assign'd
  // into one merged spec, corrupting progressive rendering.
  const patchParts = useMemo(
    () =>
      parts.filter(
        (p) =>
          p.type !== SPEC_DATA_PART_TYPE ||
          (p as { data?: { type?: string } }).data?.type !== "flat",
      ),
    [parts],
  );
  const { spec, hasSpec: hasPatchSpec } = useJsonRenderMessage(
    patchParts as unknown as DataPart[],
  );

  const segments = buildMessageSegments(parts, SPEC_DATA_PART_TYPE);
  const specInserted = segments.some((seg) => seg.kind === "spec");
  const runningToolName = firstRunningToolName(segments);
  const showDevToolCalls = useHeraldDebugToolCallsEnabled();

  const specIsRenderable = hasFlatSpecParts || (hasPatchSpec && spec != null);
  // Flat specs arrive complete; only the legacy patch path defers until stream end.
  const deferSpecRender = hasFlatSpecParts ? false : isLatestAssistantStreaming;
  const showSpecRenderer = specIsRenderable && !deferSpecRender;

  const hasVisibleContent =
    showSpecRenderer || segments.some((seg) => seg.kind === "text");

  return (
    <div ref={ref} data-herald-response="" className="contents">
      {segments.map((segment, i) => {
        const key = getSegmentKey(segment, i);
        if (segment.kind === "text") {
          return (
            <ChatMarkdown key={key} className="py-1" isMarkdownSegment>
              {segment.text}
            </ChatMarkdown>
          );
        }
        if (segment.kind === "tools") {
          if (HeraldToolCallsDevListLazy) {
            return (
              <Suspense key={key} fallback={null}>
                <HeraldToolCallsDevListLazy tools={segment.tools} />
              </Suspense>
            );
          }
          return null;
        }
        if (segment.kind === "spec" && showSpecRenderer) {
          if (segment.specIndex !== undefined) {
            if (segment.spec == null) return null;
            return (
              <div
                key={key}
                className="py-1"
                data-herald-message-segment="spec"
              >
                <HeraldSpecRenderer
                  spec={segment.spec as SpecProp}
                  fallback={UnknownComponentFallback}
                  reportDateValue={reportDateValue}
                />
              </div>
            );
          }
          // Legacy patch path.
          return (
            <div key={key} className="py-1" data-herald-message-segment="spec">
              <HeraldSpecRenderer
                spec={spec}
                fallback={UnknownComponentFallback}
                reportDateValue={reportDateValue}
              />
            </div>
          );
        }
        return null;
      })}
      {/* Legacy patch path only — specInserted is always true when hasFlatSpecParts. */}
      {showSpecRenderer && !specInserted && (
        <div className="py-1" data-herald-message-segment="spec">
          <HeraldSpecRenderer
            spec={spec}
            fallback={UnknownComponentFallback}
            reportDateValue={reportDateValue}
          />
        </div>
      )}
      {isLatestAssistantStreaming &&
        !showDevToolCalls &&
        (runningToolName !== null ? (
          <AssistantHandraiseRow>
            <div className="animate-shimmer text-sm leading-relaxed text-gray-500">
              {getToolLoadingLabel(runningToolName)}
            </div>
          </AssistantHandraiseRow>
        ) : (hasFlatSpecParts || hasPatchSpec) &&
          (!specIsRenderable || deferSpecRender) ? (
          <AssistantHandraiseRow>
            <div className="animate-shimmer text-sm leading-relaxed text-gray-500">
              Preparing response…
            </div>
          </AssistantHandraiseRow>
        ) : !hasVisibleContent ? (
          <AssistantHandraiseRow>
            <div className="animate-shimmer text-sm leading-relaxed text-gray-500">
              {DEFAULT_LABEL}
            </div>
          </AssistantHandraiseRow>
        ) : null)}
    </div>
  );
});

AssistantMessageContent.displayName = "AssistantMessageContent";
